import React from 'react';
import PropTypes from 'prop-types';

import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { useMaterialUIController } from 'context';

/* ---------- Row (Minimal) ---------- */
function ActionRow({
  icon,
  title,
  desc,
  onClick,
  color = 'info',
  ctaLabel = 'Seç',
  variant = 'text',
}) {
  const theme = useMuiTheme();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const textPrimary = darkMode ? theme.palette.white.main : theme.palette.dark.main;
  const textSecondary = darkMode
    ? alpha(theme.palette.white.main, 0.7)
    : theme.palette.text.secondary;

  const accent =
    color === 'success'
      ? theme.palette.success.main
      : color === 'warning'
      ? theme.palette.warning.main
      : color === 'dark'
      ? theme.palette.dark.main
      : theme.palette.info.main;

  return (
    <MDBox
      onClick={() => onClick?.()}
      sx={{
        display: 'grid',
        gridTemplateColumns: '34px 1fr auto',
        alignItems: 'center',
        gap: 1,
        p: 0.8,
        borderRadius: '12px',
        background: 'transparent',
        cursor: 'pointer',
        transition: 'background 140ms ease',
        '&:hover': { background: theme.palette.action.hover },
      }}
    >
      <MDBox
        sx={{
          width: 34,
          height: 34,
          borderRadius: '12px',
          display: 'grid',
          placeItems: 'center',
          background: alpha(accent, 0.1),
        }}
      >
        <Icon sx={{ color: accent, fontSize: 19 }}>{icon}</Icon>
      </MDBox>

      <MDBox sx={{ minWidth: 0 }}>
        <MDTypography
          variant="button"
          fontWeight="bold"
          noWrap
          sx={{ color: textPrimary, lineHeight: 1.15 }}
        >
          {title}
        </MDTypography>

        {desc ? (
          <MDTypography
            variant="caption"
            sx={{
              color: textSecondary,
              display: 'block',
              mt: 0.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.2,
            }}
          >
            {desc}
          </MDTypography>
        ) : null}
      </MDBox>

      <MDButton
        variant={variant}
        color={color === 'dark' ? 'dark' : color}
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
        sx={{
          borderRadius: '12px',
          px: 1,
          minWidth: 0,
          textTransform: 'none',
          fontWeight: 800,
        }}
      >
        {ctaLabel}
      </MDButton>
    </MDBox>
  );
}

/* ---------- Panel ---------- */
export default function QuickActionsPanel({
  title = 'Hızlı İşlemler',
  subtitle = 'Tek tıkla aksiyon al',
  headerIcon = 'bolt',
  chips = [],
  footerText,
  actions = [],
  bottomButtons = [], // istersen tamamen kaldırırız
  defaultOpen = false,
  storageKey = 'solisup_quick_actions_open',
  closeOnClickAway = true,
}) {
  const theme = useMuiTheme();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const textSecondary = darkMode
    ? alpha(theme.palette.white.main, 0.7)
    : theme.palette.text.secondary;

  const [open, setOpen] = React.useState(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v == null ? defaultOpen : v === '1';
    } catch {
      return defaultOpen;
    }
  });

  React.useEffect(() => {
    try {
      localStorage.setItem(storageKey, open ? '1' : '0');
    } catch {}
  }, [open, storageKey]);

  // ✅ daha kompakt
  const panelW = { xs: 270, sm: 292 };

  const OpenHeader = (
    <MDBox
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pt: 0.9,
        pb: 0.3, // ✅ boşluğu azaltan ana satır
        px: 1.25,
      }}
    >
      <MDBox sx={{ minWidth: 0 }}>
        <MDTypography variant="button" sx={{ fontWeight: 900 }} noWrap>
          {title}
        </MDTypography>

        {subtitle ? (
          <MDTypography variant="caption" sx={{ opacity: 0.7, fontWeight: 700 }} noWrap>
            {subtitle}
          </MDTypography>
        ) : null}
      </MDBox>

      <Icon
        sx={({ palette: { dark, white } }) => ({
          cursor: 'pointer',
          color: darkMode ? white.main : dark.main,
        })}
        onClick={() => setOpen(false)}
      >
        close
      </Icon>
    </MDBox>
  );

  return (
    <>
      {/* ✅ Overlay (drawer hissi) */}
      {open ? (
        <MDBox
          onClick={() => setOpen(false)}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1290,
            background: (t) => alpha(t.palette.common.black, 0.18),
          }}
        />
      ) : null}

      <ClickAwayListener
        onClickAway={() => {
          if (closeOnClickAway && open) setOpen(false);
        }}
      >
        {/* ✅ anchor: sağ alt */}
        <MDBox
          sx={{
            position: 'fixed',
            right: { xs: 10, sm: 18 },
            bottom: { xs: 10, sm: 18 },
            zIndex: 1300,
          }}
        >
          {/* ✅ Drawer-like panel: ikonun SOLUNDA açılır, ikon görünür kalır */}
          <MDBox
            sx={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              transform: open ? 'translateX(-68px)' : 'translateX(0)',
              pointerEvents: open ? 'auto' : 'none',
            }}
          >
            <Card
              sx={(t) => ({
                width: panelW,
                overflow: 'hidden',
                borderRadius: '16px',
                boxShadow: t.shadows[8],
                background: t.palette.background.paper,
                border: `1px solid ${t.palette.divider}`,
                opacity: open ? 1 : 0,
                transform: open ? 'translateX(0)' : 'translateX(10px)',
                transition: 'opacity 160ms ease, transform 160ms ease',
              })}
            >
              {OpenHeader}
              <Divider sx={{ opacity: 0.6 }} />

              {/* ✅ body daha sıkı */}
              <MDBox p={1} sx={{ display: 'grid', gap: 0.5 }}>
                {/* ✅ chips: tek satır + scroll */}
                {chips?.length ? (
                  <MDBox
                    sx={{
                      display: 'flex',
                      gap: 0.5,
                      overflowX: 'auto',
                      whiteSpace: 'nowrap',
                      pb: 0.25,
                      '&::-webkit-scrollbar': { height: 6 },
                    }}
                  >
                    {chips.map((c) => (
                      <Chip
                        key={typeof c === 'string' ? c : c.label}
                        size="small"
                        label={typeof c === 'string' ? c : c.label}
                        sx={{
                          bgcolor: (t) => t.palette.action.selected,
                          border: (t) => `1px solid ${t.palette.divider}`,
                          height: 22,
                        }}
                      />
                    ))}
                  </MDBox>
                ) : null}

                {/* ✅ footer: info strip */}
                {footerText ? (
                  <MDBox
                    sx={(t) => ({
                      p: 0.75,
                      borderRadius: '12px',
                      background: alpha(t.palette.info.main, 0.08),
                      border: `1px solid ${alpha(t.palette.info.main, 0.18)}`,
                    })}
                  >
                    <MDTypography variant="caption" sx={{ color: textSecondary }}>
                      {footerText}
                    </MDTypography>
                  </MDBox>
                ) : null}

                {/* actions */}
                {(actions || [])
                  .filter((a) => !a.hidden)
                  .map((a) => {
                    const { key, ...rowProps } = a;
                    return <ActionRow key={key} {...rowProps} />;
                  })}

                {/* bottom buttons */}
                {bottomButtons?.length ? <Divider sx={{ my: 0.25, opacity: 0.6 }} /> : null}

                {bottomButtons?.length ? (
                  <MDBox sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
                    {bottomButtons.slice(0, 2).map((b) => (
                      <MDButton
                        key={b.key}
                        variant={b.variant || 'outlined'}
                        color={b.color || 'info'}
                        fullWidth
                        sx={{
                          borderRadius: '14px',
                          py: 0.85,
                          textTransform: 'none',
                          fontWeight: 800,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          b.onClick?.();
                        }}
                      >
                        {b.label}
                      </MDButton>
                    ))}
                  </MDBox>
                ) : null}
              </MDBox>
            </Card>
          </MDBox>

          {/* ✅ FAB ikon: her zaman görünür */}
          <MDBox
            onClick={() => setOpen((v) => !v)}
            sx={{
              width: 54,
              height: 54,
              borderRadius: '16px',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
              boxShadow: (t) => t.shadows[8],
              background: (t) => t.palette.background.paper,
              border: (t) => `1px solid ${t.palette.divider}`,
              transition: 'transform 160ms ease, box-shadow 160ms ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: (t) => t.shadows[10],
              },
            }}
          >
            <Icon sx={(t) => ({ color: t.palette.info.main, fontSize: 22 })}>
              {open ? 'close' : headerIcon}
            </Icon>
          </MDBox>
        </MDBox>
      </ClickAwayListener>
    </>
  );
}

QuickActionsPanel.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  headerIcon: PropTypes.string,
  chips: PropTypes.array,
  footerText: PropTypes.node,
  actions: PropTypes.array,
  bottomButtons: PropTypes.array,
  defaultOpen: PropTypes.bool,
  storageKey: PropTypes.string,
  closeOnClickAway: PropTypes.bool,
};
